import React from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import './Login.less';
import HwzComponent from '../../common/component/HwzComponent';
import { Button, Col, Form, Input, Row, Alert, Tooltip, Spin } from 'antd';
import User from '../../common/model/user/User';
import Moon from '../../common/model/global/Moon';
import { LockOutlined, UserOutlined, DownloadOutlined, ExclamationCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import Sun from '../../common/model/global/Sun';
import MessageBoxUtil from '../../common/util/MessageBoxUtil';
import Preference from '../../common/model/preference/Preference';
import Lang from '../../common/model/global/Lang';
import { AllowedDeviceType } from '../../common/model/user/User';

interface IProps extends RouteComponentProps {}

interface IState {
  deviceInfo: any;
  loading: boolean;
  loginLoading: boolean;
  autoLoginAttempted: boolean;
  autoLoginStatus: 'idle' | 'processing' | 'success' | 'error';
  autoLoginError: string;
  deviceInfoLoaded: boolean;
  deviceInfoError: boolean;
  hasCacheData: boolean;
}

export default class Login extends HwzComponent<IProps, IState> {
  user: User = Moon.getSingleton().user;
  private abortController: AbortController | null = null;
  private formRef: any = React.createRef();
  private deviceInfoTimeout: NodeJS.Timeout | null = null;

  constructor(props: IProps) {
    super(props);
    this.state = {
      deviceInfo: null,
      loading: false,
      loginLoading: false,
      autoLoginAttempted: false,
      autoLoginStatus: 'idle',
      autoLoginError: '',
      deviceInfoLoaded: false,
      deviceInfoError: false,
      hasCacheData: false
    };
  }

  componentDidMount() {
    console.log('Login组件挂载，开始初始化');
    
    this.logout();
    this.loadDeviceInfoFromCache();
    this.fetchDeviceInfo();
    this.checkUrlForAutoLogin();
  }

  componentWillUnmount() {
    console.log('Login组件卸载，清理资源');
    
    if (this.abortController) {
      this.abortController.abort();
    }
    
    if (this.deviceInfoTimeout) {
      clearTimeout(this.deviceInfoTimeout);
    }
  }

  checkUrlForAutoLogin() {
    console.log('检查自动登录参数');
    
    if (this.user.uuid) {
      this.props.history.push('/');
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const usernameParam = urlParams.get('username');
    const passwordParam = urlParams.get('password');
    
    if (usernameParam && passwordParam) {
      const username = decodeURIComponent(usernameParam);
      const password = decodeURIComponent(passwordParam);
      console.log('从URL参数解析到登录信息，开始自动登录');
      this.clearUrlCredentials();
      this.autoLogin(username, password);
      return;
    }

    if (window.location.hash && window.location.hash.includes('#')) {
      const hash = window.location.hash.substring(1);
      const parts = hash.split('#');
      if (parts.length >= 2) {
        const username = decodeURIComponent(parts[0].trim());
        const password = decodeURIComponent(parts[1].trim());
        console.log('从URL hash解析到登录信息，开始自动登录');
        this.clearUrlCredentials();
        this.autoLogin(username, password);
        return;
      }
    }
    
    console.log('未找到自动登录参数，显示登录界面');
  }

  clearUrlCredentials() {
    try {
      console.log('清除URL中的登录凭证');
      
      if (window.location.hash) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
      
      const url = new URL(window.location.href);
      url.searchParams.delete('username');
      url.searchParams.delete('password');
      window.history.replaceState(null, '', url.toString());
    } catch (error) {
      console.error('清除URL参数失败:', error);
    }
  }

  logout() {
    console.log('执行登出操作');
    
    localStorage.removeItem('user_uuid');
    localStorage.removeItem('user_token');
    localStorage.removeItem('user_info');
    
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('force_logout_')) {
        localStorage.removeItem(key);
      }
    });
    
    this.user.httpLogout(function () {
      Sun.updateFrame();
    });
  }

  loadDeviceInfoFromCache() {
    const cachedDeviceInfo = localStorage.getItem('device_info');
    if (cachedDeviceInfo) {
      try {
        const deviceInfo = JSON.parse(cachedDeviceInfo);
        console.log('从缓存加载设备信息成功');
        
        this.setState({
          deviceInfo: deviceInfo,
          deviceInfoLoaded: true,
          hasCacheData: true,
          deviceInfoError: false
        });
      } catch (e) {
        console.warn('解析设备信息缓存失败:', e);
        this.setState({
          deviceInfoLoaded: true,
          hasCacheData: false,
          deviceInfoError: true
        });
      }
    } else {
      console.log('缓存中没有设备信息');
      this.setState({
        deviceInfoLoaded: true,
        hasCacheData: false
      });
    }
  }

  async fetchDeviceInfo(): Promise<void> {
    console.log('开始获取设备信息（接口）');
    
    this.setState({ loading: true });
    
    return new Promise<void>((resolve) => {
      this.deviceInfoTimeout = setTimeout(() => {
        console.log('获取设备信息超时（3秒）');
        
        if (this.deviceInfoTimeout) {
          clearTimeout(this.deviceInfoTimeout);
          this.deviceInfoTimeout = null;
        }
        
        if (this.state.hasCacheData) {
          this.setState({ 
            loading: false,
            deviceInfoError: false
          });
        } else {
          this.setState({ 
            loading: false,
            deviceInfoError: true
          });
        }
        resolve();
      }, 3000);

      fetch('http://localhost:9005/sys/getlocalinfo', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })
        .then(response => {
          if (this.deviceInfoTimeout) {
            clearTimeout(this.deviceInfoTimeout);
            this.deviceInfoTimeout = null;
          }
          
          if (!response.ok) {
            console.warn('设备信息接口响应失败:', response.status);
            throw new Error(`HTTP ${response.status}`);
          }
          
          return response.json();
        })
        .then(data => {
          console.log('设备信息接口响应成功');
          
          if (data && data.data && data.data.deviceInfo) {
            const deviceInfo = data.data.deviceInfo;
            
            this.setState({
              deviceInfo: deviceInfo,
              loading: false,
              deviceInfoError: false,
              hasCacheData: true
            });
            
            localStorage.setItem('device_info', JSON.stringify(deviceInfo));
            console.log('设备信息已保存到缓存');
          } else {
            console.warn('设备信息格式不正确');
            this.setState({
              loading: false,
              deviceInfoError: !this.state.hasCacheData
            });
          }
        })
        .catch(error => {
          if (this.deviceInfoTimeout) {
            clearTimeout(this.deviceInfoTimeout);
            this.deviceInfoTimeout = null;
          }
          
          console.log('获取设备信息失败:', error.message);
          
          if (this.state.hasCacheData) {
            this.setState({ 
              loading: false,
              deviceInfoError: false
            });
          } else {
            this.setState({ 
              loading: false,
              deviceInfoError: true
            });
          }
        })
        .finally(() => {
          resolve();
        });
    });
  }

  autoLogin(username: string, password: string) {
    if (this.state.autoLoginAttempted) {
      console.log('自动登录已尝试过，跳过');
      return;
    }
    
    console.log('开始自动登录流程，用户名:', username);
    
    this.setState({
      autoLoginAttempted: true,
      autoLoginStatus: 'processing',
      loginLoading: true
    });
    
    if (this.formRef.current) {
      this.formRef.current.setFieldsValue({
        username: username,
        password: password
      });
    }
    
    this.performAutoLogin(username, password);
  }

  performAutoLogin(username: string, password: string) {
    const loginParams = {
      username: username,
      password: password,
      device: AllowedDeviceType.WEB,
      ...this.prepareDeviceInfoParams()
    };
    
    console.log('发送自动登录请求，参数:', loginParams);

    this.user.httpLoginWithParams(
      loginParams,
      (response: any) => {
        console.log('自动登录请求成功，响应:', response);
        
        if (response && response.success) {
          console.log('自动登录成功，准备跳转');
          
          const preference: Preference = Moon.getSingleton().preference;
          const ctrlMod = preference.ctrlMod || 0;
          let targetPath = '/';
          
          if (ctrlMod === 3) {
            targetPath = '/removable/list';
          }
          
          this.setState({
            autoLoginStatus: 'success',
            loginLoading: false
          });
          
          console.log(`自动登录成功，跳转到: ${targetPath}`);
          window.location.href = targetPath;
        } else {
          const errorMsg = response?.message || Lang.t('user.loginFailed');
          console.log('自动登录失败:', errorMsg);
          
          this.setState({
            autoLoginStatus: 'error',
            autoLoginError: errorMsg,
            loginLoading: false
          });
          
          MessageBoxUtil.error(errorMsg);
        }
      },
      (error: any) => {
        console.error('自动登录请求失败:', error);
        
        const errorMsg = error?.message || Lang.t('user.loginFailed');
        this.setState({
          autoLoginStatus: 'error',
          autoLoginError: errorMsg,
          loginLoading: false
        });
        
        MessageBoxUtil.error(errorMsg);
      }
    );
  }

  onFinish(values: any) {
    if (this.state.autoLoginStatus === 'processing') {
      console.log('自动登录进行中，忽略手动登录');
      return;
    }
    
    console.log('手动登录，用户名:', values['username']);
    
    this.setState({ loginLoading: true });
    
    const loginParams = {
      username: values['username'],
      password: values['password'],
      device: AllowedDeviceType.WEB,
      ...this.prepareDeviceInfoParams()
    };
    
    this.user.httpLoginWithParams(
      loginParams,
      (response: any) => {
        MessageBoxUtil.success(Lang.t('user.loginSuccess'));
        
        const preference: Preference = Moon.getSingleton().preference;
        const ctrlMod = preference.ctrlMod || 0;
        let targetPath = '/';
        
        if (ctrlMod === 3) {
          targetPath = '/removable/list';
        }
        
        console.log(`手动登录成功，跳转到: ${targetPath}`);
        window.location.href = targetPath;
        
        this.setState({ loginLoading: false });
      },
      (error: any) => {
        this.setState({ loginLoading: false });
      }
    );
  }

  onFinishFailed(errorInfo: any) {
    console.log('表单验证失败:', errorInfo);
  }

  /**
   * 从浏览器 userAgent 解析操作系统信息（同步方法）
   * @returns 操作系统名称，若无法识别则返回 'unknown'
   */

getOSFromUserAgent(): string {
  const ua = navigator.userAgent;
  // Windows 检测（保持最前，因为 Windows 不会与其它冲突）
  if (ua.indexOf('Windows NT 10.0') !== -1) return 'Windows 10';
  if (ua.indexOf('Windows NT 6.3') !== -1) return 'Windows 8.1';
  if (ua.indexOf('Windows NT 6.2') !== -1) return 'Windows 8';
  if (ua.indexOf('Windows NT 6.1') !== -1) return 'Windows 7';
  if (ua.indexOf('Windows NT 6.0') !== -1) return 'Windows Vista';
  if (ua.indexOf('Windows NT 5.1') !== -1) return 'Windows XP';
  // macOS 检测
  if (ua.indexOf('Mac OS X') !== -1) return 'macOS';
  // 移动操作系统优先于 Linux（因为 Android/iOS UA 常包含 "Linux"）
  if (ua.indexOf('Android') !== -1) return 'Android';
  if (ua.indexOf('iOS') !== -1 || ua.indexOf('iPhone') !== -1 || ua.indexOf('iPad') !== -1) return 'iOS';
  // 桌面 Linux（不包含 Android/iOS 特征）
  if (ua.indexOf('Linux') !== -1) return 'Linux';
  if (ua.indexOf('Chrome OS') !== -1) return 'Chrome OS';
  return 'unknown';
}

  /**
   * 准备设备信息参数，用于登录请求
   * - 优先使用 state 中缓存的 deviceInfo
   * - 若字段缺失或为 'unknown'，则尝试从网页端再次获取（当前支持 os_info 通过 userAgent 解析）
   * - Web 端无法获取的字段（client_ip、client_mac、disk_serial、device_name）保持 'unknown'
   * - 始终添加 login_time 和 source 字段
   */
  prepareDeviceInfoParams() {
    const { deviceInfo } = this.state;
    
    // 初始值：优先从 deviceInfo 中取值，否则为 'unknown'
    let client_ip = 'unknown';
    let client_mac = 'unknown';
    let disk_serial = 'unknown';
    let device_name = 'unknown';
    let os_info = 'unknown';
    
    if (deviceInfo) {
      client_ip = deviceInfo.ip || 'unknown';
      client_mac = deviceInfo.mac || 'unknown';
      disk_serial = deviceInfo.diskSerial || 'unknown';
      device_name = deviceInfo.deviceName || 'unknown';
      os_info = deviceInfo.os || 'unknown';
    }
    
    // 对于能从网页同步获取的字段，在值为 'unknown' 时再次尝试获取
    // 1. 操作系统信息：通过 userAgent 解析
    if (os_info === 'unknown') {
      const webOS = this.getOSFromUserAgent();
      if (webOS !== 'unknown') {
        os_info = webOS;
      }
    }
    
    // 2. client_ip：浏览器无法同步获取（需异步请求外部服务），跳过，保持 'unknown'
    // 3. device_name：浏览器无法获取主机名，跳过
    // 4. client_mac / disk_serial：Web 端完全无法获取，跳过
    
    return {
      client_ip,
      client_mac,
      disk_serial,
      device_name,
      os_info,
      login_time: new Date().toISOString(),
      source: 'WEB'
    };
  }

  handleRefreshDeviceInfo = () => {
    console.log('刷新设备信息');
    this.fetchDeviceInfo();
  }

  isDeviceFeatureSupported(): boolean {
    const { deviceInfo } = this.state;
    return !!(deviceInfo && deviceInfo.mac && deviceInfo.diskSerial);
  }

  handleDownload = (url: string, filename: string) => {
    console.log('开始下载:', filename);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  renderLoadingOverlay() {
    return (
      <div className="login-loading-overlay">
        <div className="loading-content">
          <Spin 
            indicator={<LoadingOutlined style={{ fontSize: 48, color: '#1890ff' }} spin />} 
            size="large"
          />
          <div style={{ marginTop: 20, fontSize: 16, color: '#666' }}>
            {Lang.t('loading')}
          </div>
          <div style={{ marginTop: 10, fontSize: 12, color: '#999' }}>
            {Lang.t('autoLogin.pleaseWait')}
          </div>
        </div>
      </div>
    );
  }

  renderDeviceInfoPanel() {
    const { deviceInfo, loading, deviceInfoError, hasCacheData } = this.state;
    const isDeviceSupported = this.isDeviceFeatureSupported();
    
    if (!deviceInfo) {
      return null;
    }
    
    return (
      <div className="device-info-panel">
        <div className="device-info-header">
          {Lang.t('pcInfoRecognize')}
        </div>
        <div className="device-info-content">
          <div className="device-info-row">
            <span className="label">IP:</span>
            <span className="value">{deviceInfo.ip || Lang.t('unknown')}</span>
          </div>
          <div className="device-info-row">
            <span className="label">MAC:</span>
            <span className="value">{deviceInfo.mac || Lang.t('unknown')}</span>
          </div>
          <div className="device-info-row">
            <span className="label">{Lang.t('computer.pcName')}:</span>
            <span className="value">{deviceInfo.deviceName || Lang.t('unknown')}</span>
          </div>
          <div className="device-info-row">
            <span className="label">{Lang.t('layout.onlinePC')}:</span>
            <span className="value">{deviceInfo.os || Lang.t('unknown')}</span>
          </div>
          
          {!hasCacheData && deviceInfoError && (
            <div className="device-info-error">
              <ExclamationCircleOutlined /> 
              <span>{Lang.t('device.fetchFailed')}</span>
            </div>
          )}
          
          {hasCacheData && deviceInfoError && (
            <div className="device-info-warning">
              <ExclamationCircleOutlined /> 
              <span>{Lang.t('device.usingCache')}</span>
            </div>
          )}
          
          {!isDeviceSupported && (
            <div className="device-info-not-supported">
              <ExclamationCircleOutlined /> 
              <span>{Lang.t('device.notSupported')}</span>
            </div>
          )}
          
          <button 
            className="refresh-device-btn"
            onClick={this.handleRefreshDeviceInfo}
            disabled={loading}
          >
            {loading ? Lang.t('getting') : Lang.t('freshPcInfo')}
          </button>
        </div>
      </div>
    );
  }

  renderAutoLoginStatus() {
    const { autoLoginStatus, autoLoginError } = this.state;
    
    if (autoLoginStatus === 'processing') {
      return (
        <div className="auto-login-processing">
          <Spin 
            indicator={<LoadingOutlined style={{ fontSize: 24, color: '#1890ff' }} spin />} 
            size="small"
          />
          <span style={{ marginLeft: 10 }}>{Lang.t('autoLogin.loggingIn')}</span>
        </div>
      );
    }
    
    if (autoLoginStatus === 'error') {
      return (
        <Alert
          message={Lang.t('autoLogin.failed')}
          description={autoLoginError}
          type="error"
          showIcon
          className="auto-login-alert"
          closable
          onClose={() => this.setState({ autoLoginStatus: 'idle' })}
        />
      );
    }
    
    return null;
  }

  renderLoginForm() {
    const { loginLoading, autoLoginStatus, deviceInfoLoaded } = this.state;
    const preference: Preference = Moon.getSingleton().preference;
    const baseUrl = window.location.origin;
    const manualFileName = preference.ctrlMod === 3 ? 'HWZUMidUsermanual.pdf' : 'HWZUsermanual.pdf';
    const manualUrl = `${baseUrl}/HWZUsermanual.pdf?t=${Date.now()}`;
    const clientUrl = `${baseUrl}/APP/HWZWinApp.msi?t=${Date.now()}`;
    const secAgentUrl = `${baseUrl}/APP/HWZWinSecAgent.msi?t=${Date.now()}`;
    const androidClientUrl = `${baseUrl}/APP/AndroidApp.apk?t=${Date.now()}`;

    // 获取当前操作系统类型
    const currentOS = this.getOSFromUserAgent();
    const isWindows = currentOS.includes('Windows');
    const isAndroid = currentOS === 'Android';
    
    if (!deviceInfoLoaded) {
      return this.renderLoadingOverlay();
    }
    
    return (
      <div className="user-login">
        {this.renderDeviceInfoPanel()}
        
        <Row className="login-container">
          <Col xs={{ span: 24 }} md={{ span: 8, offset: 6 }}>
            <div className="welcome">{Lang.t('user.welcomeLogin')}</div>
            
            {this.renderAutoLoginStatus()}
            
            <Form
              name="basic"
              ref={this.formRef}
              initialValues={{ remember: true }}
              onFinish={this.onFinish.bind(this)}
              onFinishFailed={this.onFinishFailed.bind(this)}
              className="login-form"
            >
              <Form.Item
                name="username"
                rules={[
                  { required: true, message: Lang.t('user.enterUsername') },
                ]}
              >
                <Input
                  size="large"
                  placeholder={Lang.t('user.enterUsername')}
                  prefix={<UserOutlined />}
                  disabled={autoLoginStatus === 'processing'}
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[
                  { required: true, message: Lang.t('user.enterPassword') },
                ]}
              >
                <Input.Password
                  size="large"
                  placeholder={Lang.t('user.enterPassword')}
                  prefix={<LockOutlined />}
                  disabled={autoLoginStatus === 'processing'}
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  icon={<UserOutlined />}
                  block={true}
                  htmlType="submit"
                  loading={loginLoading || autoLoginStatus === 'processing'}
                  disabled={loginLoading || autoLoginStatus === 'processing'}
                  className="login-btn"
                >
                  {autoLoginStatus === 'processing' ? Lang.t('autoLogin.loggingIn') : 
                   loginLoading ? Lang.t('user.loggingIn') : Lang.t('user.login')}
                </Button>
              </Form.Item>

              {preference.allowRegister && (
                <div className="text-right">
                  <Link to={'/user/register'}>
                    <span className="link">{Lang.t('user.toToRegister')}</span>
                  </Link>
                </div>
              )}
              
              <div className="download-links">
                {/* 用户手册 - 始终显示 */}
                <div className="download-link">
                  <DownloadOutlined />
                  <a 
                    href={manualUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.preventDefault();
                      this.handleDownload(manualUrl, manualFileName);
                    }}
                  >
                    {Lang.t('downloadusermanual')}
                  </a>
                </div>

                {/* 仅 Android 系统显示安卓客户端下载链接 */}
                {isAndroid && (
                  <div className="download-link">
                    <DownloadOutlined />
                    <a
                      href={androidClientUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        e.preventDefault();
                        this.handleDownload(androidClientUrl, 'AndroidApp.apk');
                      }}
                    >
                      {Lang.t('downandroidapp') || '下载安卓客户端'}
                    </a>
                  </div>
                )}

                {/* 仅 Windows 系统显示 Windows 客户端和安全代理 */}
                {isWindows && (
                  <>
                    {/* Windows 客户端：仅在 ctrlMod !== 3 时显示 */}
                    {preference.ctrlMod !== 3 && (
                      <div className="download-link">
                        <DownloadOutlined />
                        <a
                          href={clientUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            e.preventDefault();
                            this.handleDownload(clientUrl, 'HWZWinApp.msi');
                          }}
                        >
                          {Lang.t('downwinapp')}
                        </a>
                      </div>
                    )}
                    {/* Windows 安全代理 */}
                    <div className="download-link">
                      <DownloadOutlined />
                      <Tooltip title={Lang.t('secAgent.installTip')}>
                        <a 
                          href={secAgentUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            e.preventDefault();
                            this.handleDownload(secAgentUrl, 'HWZWinSecAgent.msi');
                          }}
                        >
                          {Lang.t('downwinsecagent')}
                        </a>
                      </Tooltip>
                    </div>
                  </>
                )}
              </div>
            </Form>
          </Col>
        </Row>
      </div>
    );
  }

  render() {
    return this.renderLoginForm();
  }
}